import { useMemo } from "react";
import {
  ConstructionSection,
  IdentificationSection,
  LbcsSection,
  LocationSection,
  MarketSection,
  RiskSection,
} from "./PropertyRegridSnapshotSections";
import {
  BlueprintPreview,
  BuildingCard,
  safeParseJson,
} from "./PropertyRegridSnapshotBlueprint";
import { SectionTitle } from "./PropertyRegridSnapshotElements";

const shortDateFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function SnapshotHeader({ parcel, score, fetchedAt }) {
  return (
    <div className="px-4 py-3 border-b border-gray-100 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">
          Snapshot Regrid
        </div>
        <div className="text-lg font-semibold text-gray-900">
          Parcel {parcel || "—"}
        </div>
        <div className="text-[11px] text-gray-500">
          Atualizado em {fetchedAt || "—"}
        </div>
      </div>
      {score !== null && score !== undefined && (
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">
            Score
          </div>
          <div className="text-2xl font-semibold text-sky-600">
            {Number(score).toFixed(1)}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return shortDateFormatter.format(date);
}

export default function PropertyRegridSnapshotCard({ snapshot }) {
  const blueprintData = useMemo(() => {
    const parcelGeometry = safeParseJson(snapshot?.parcel_geometry_json);
    const buildingGeoms = (snapshot?.buildings ?? [])
      .map((building) => safeParseJson(building.geometry_json))
      .filter(Boolean);
    return { parcelGeometry, buildingGeoms };
  }, [snapshot]);

  if (!snapshot) {
    return (
      <div className="border border-gray-200 rounded-lg bg-white p-4">
        <div className="text-sm font-semibold text-gray-900">
          Regrid Snapshot
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Sincronize com o Regrid para ver mais informações
        </p>
      </div>
    );
  }

  const buildings = snapshot.buildings ?? [];
  const fetchedAt = formatDate(snapshot.fetched_at);

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
      <SnapshotHeader
        parcel={snapshot.parcel_number}
        score={snapshot.regrid_score}
        fetchedAt={fetchedAt}
      />
      <div className="p-4 space-y-4">
        <IdentificationSection snapshot={snapshot} />
        <LocationSection snapshot={snapshot} />
        <ConstructionSection snapshot={snapshot} />
        <MarketSection snapshot={snapshot} />
        <RiskSection snapshot={snapshot} />
        <LbcsSection snapshot={snapshot} />

        <div>
          <SectionTitle title="Lote e construções" />
          <div className="flex items-center justify-between mb-2 text-[11px] uppercase tracking-wider text-gray-500">
            <div>Polygon info</div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-sky-400 rounded-sm inline-block" />
                Lote
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-emerald-400 rounded-sm inline-block" />
                Construções
              </span>
            </div>
          </div>
          <BlueprintPreview
            parcelGeometry={blueprintData.parcelGeometry}
            buildingGeometries={blueprintData.buildingGeoms}
          />
        </div>

        {buildings.length > 0 && (
          <div className="space-y-2">
            <SectionTitle title={`Construções (${buildings.length})`} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {buildings.map((building) => (
                <BuildingCard key={building.id} building={building} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
